<template>
  <div :class="$style.embedded">
    <!-- PayPal / wallet buttons (v6 <paypal-button> web component is appended here). -->
    <div ref="buttonContainer" v-show="showButtons" :class="$style.buttons"></div>

    <!-- Advanced (unbranded) card fields. Hosted PayPal iframes are appended into the refs. -->
    <div v-show="showCard" :class="$style.cardFields">
      <label :class="$style.label">{{ $t('UiCardField.cardNumber') }}</label>
      <div ref="numberField" :class="$style.field"></div>

      <div :class="$style.row">
        <div :class="$style.col">
          <label :class="$style.label">{{ $t('FormSectionBankCard.expiryDate') }}</label>
          <div ref="expiryField" :class="$style.field"></div>
        </div>
        <div :class="$style.col">
          <label :class="$style.label">{{ $t('FormSectionBankCard.cvv') }}</label>
          <div ref="cvvField" :class="$style.field"></div>
        </div>
      </div>

      <UiButton :disabled="submitting" @click="payWithCard">
        {{ $t('FormSection.payButtonPrefix') }}
      </UiButton>
    </div>

    <!-- Save-without-purchase (PayPal Vault). Vaults a payment method for future use via a
         setup token; no charge occurs. Rendered only when the save flow is eligible. -->
    <div v-show="showSavePaypal || showSaveCard" :class="$style.saveBlock">
      <div :class="$style.saveHeading">Save for later</div>

      <div ref="savePaypalContainer" v-show="showSavePaypal" :class="$style.buttons"></div>

      <div v-show="showSaveCard" :class="$style.cardFields">
        <label :class="$style.label">{{ $t('UiCardField.cardNumber') }}</label>
        <div ref="saveNumberField" :class="$style.field"></div>

        <div :class="$style.row">
          <div :class="$style.col">
            <label :class="$style.label">{{ $t('FormSectionBankCard.expiryDate') }}</label>
            <div ref="saveExpiryField" :class="$style.field"></div>
          </div>
          <div :class="$style.col">
            <label :class="$style.label">{{ $t('FormSectionBankCard.cvv') }}</label>
            <div ref="saveCvvField" :class="$style.field"></div>
          </div>
        </div>

        <UiButton :disabled="savingCard" @click="saveCard">
          Save card
        </UiButton>
      </div>
    </div>
  </div>
</template>

<script>
// Imported from the real dist path, NOT '@paypal/paypal-js/sdk-v6': that subpath is only
// exposed through the package's "exports" map, which webpack 4 (vue-cli) ignores — so the
// package-name subpath fails to resolve. The direct path is the ESM v6 build.
import { loadCoreSdkScript } from '@paypal/paypal-js/dist/v6/esm/paypal-js';
import axios from 'axios';
import { apiUrl, paypalEmbeddedClientId, buildPurpose } from '@/constants';

// The v6 SDK requires an explicit environment (a 10.0.0 breaking change — omitting it used
// to silently default to sandbox). Mirror the live/sandbox split used for the client ids.
const paypalEnvironment = (buildPurpose === 'release' || buildPurpose === 'bluegreen')
  ? 'production'
  : 'sandbox';

export default {
  name: 'PaypalEmbeddedFields',
  props: {
    chargeAmount: {
      type: Number,
      required: true,
    },
    token: {
      type: String,
      required: true,
    },
    orderId: {
      type: String,
      required: true,
    },
    merchantId: {
      type: String,
      required: true,
    },
    currency: {
      type: String,
      default: 'USD',
      required: false,
    },
  },
  data() {
    return {
      clientId: paypalEmbeddedClientId,
      cardSession: null,
      saveCardSession: null,
      // Both start hidden (v-show) so nothing flashes before eligibility is confirmed; each
      // section is revealed only after its own eligibility check + render succeeds.
      showButtons: false,
      showCard: false,
      showSavePaypal: false,
      showSaveCard: false,
      submitting: false,
      savingCard: false,
    };
  },
  async mounted() {
    try {
      // v6 core SDK: one script + one namespace, serving both the PayPal buttons and the
      // card fields. Independent from Paypal.vue's classic v5 buttons SDK.
      const coreSdk = await loadCoreSdkScript({
        clientId: this.clientId,
        environment: paypalEnvironment,
        dataNamespace: 'paypal_embedded',
      });
      if (!coreSdk) {
        this.$emit('card-fields-ineligible');
        return;
      }

      const sdkInstance = await coreSdk.createInstance({
        clientId: this.clientId,
        components: ['paypal-payments', 'venmo-payments', 'card-fields'],
        pageType: 'checkout',
        partnerAttributionId: 'PAYARC_PSP',
        merchantId: this.merchantId,
        // Sandbox-only: forces the buyer country so eligibility can be exercised in test.
        ...(paypalEnvironment === 'sandbox' ? { testBuyerCountry: 'US' } : {}),
      });

      const methods = await sdkInstance.findEligibleMethods({ currencyCode: this.currency });
      // Each capability sets up independently: a throw in one must not abort the others or
      // emit a misleading top-level error after siblings already rendered.
      this.safeSetup('buttons', () => this.setupButtons(sdkInstance, methods));
      this.safeSetup('venmo', () => this.setupVenmoButton(sdkInstance, methods));
      this.safeSetup('payLater', () => this.setupPayLater(sdkInstance, methods));
      // this.safeSetup('credit', () => this.setupCredit(sdkInstance, methods));
      // this.safeSetup('cardFields', () => this.setupCardFields(sdkInstance, methods));
      // this.safeSetup('paypalSave', () => this.setupPaypalSave(sdkInstance, methods));
      // this.safeSetup('cardFieldsSave', () => this.setupCardFieldsSave(sdkInstance, methods));

      if (!this.showButtons && !this.showCard) {
        this.$emit('card-fields-ineligible');
      }
    } catch (error) {
      console.error('Failed to load PayPal embedded fields (v6):', error);
      this.$emit('card-fields-ineligible');
    }
  },
  methods: {
    // Venmo: US merchants/buyers and USD only — findEligibleMethods enforces that.
    setupVenmoButton(sdkInstance, methods) {
      if (!methods.isEligible('venmo')) {
        return;
      }
      const session = sdkInstance.createVenmoOneTimePaymentSession({
        // Same capture path as the PayPal button: FormSection's paypalPay -> capture.
        onApprove: () => {
          this.$emit('paypal-payment-success');
          return Promise.resolve();
        },
        onCancel: () => {},
        onError: () => this.$emit('paypal-payment-error'),
      });

      const button = document.createElement('venmo-button');
      button.type = 'pay';
      button.style.display = 'block';
      button.style.width = '100%';
      button.addEventListener('click', async () => {
        try {
          // 'auto' is the only presentation mode Venmo supports.
          await session.start({ presentationMode: 'auto' }, this.createOrder());
        } catch (error) {
          this.$emit('paypal-payment-error');
        }
      });
      this.$refs.buttonContainer.appendChild(button);
      this.showButtons = true;
    },
    // Run one capability's setup in isolation so a failure can't cascade to the others.
    safeSetup(label, fn) {
      try {
        fn();
      } catch (error) {
        console.error(`PayPal embedded: "${label}" setup failed`, error);
      }
    },

    // Shared order creation for both flows — POST /orders/paypal/ returns the PayPal order id.
    // v6 expects createOrder to resolve to { orderId }.
    async createOrder() {
      const { data } = await axios.post(`${apiUrl}/orders/paypal/`, {
        order_id: this.orderId,
        amount: this.chargeAmount,
      }, {
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      });
      return { orderId: data.data.paypal_order_id };
    },

    setupButtons(sdkInstance, methods) {
      if (!methods.isEligible('paypal')) {
        return;
      }
      const session = sdkInstance.createPayPalOneTimePaymentSession({
        // onApprove fans out to FormSection's paypalPay -> capture, same as the classic flow.
        onApprove: () => {
          this.$emit('paypal-payment-success');
          return Promise.resolve();
        },
        onCancel: () => {},
        onError: () => this.$emit('paypal-payment-error'),
      });

      // The v6 button is a custom element; created imperatively (not in the Vue template) to
      // avoid Vue 2 "unknown custom element" warnings. Clicking it starts the payment session.
      const button = document.createElement('paypal-button');
      button.type = 'pay';
      // The web component's host defaults to display:inline, so it shrinks to its content.
      // Force it to fill the container width.
      button.style.display = 'block';
      button.style.width = '100%';
      button.addEventListener('click', async () => {
        try {
          // 'auto' lets the SDK pick popup/modal/redirect — safest inside an iframe.
          await session.start({ presentationMode: 'auto' }, this.createOrder());
        } catch (error) {
          this.$emit('paypal-payment-error');
        }
      });
      this.$refs.buttonContainer.appendChild(button);
      this.showButtons = true;
    },

    setupPayLater(sdkInstance, methods) {
      if (!methods.isEligible('paylater')) {
        return;
      }
      // countryCode/productCode come from eligibility details — the pay-later button won't
      // render unless BOTH are set on the element.
      const { countryCode, productCode } = methods.getDetails('paylater');

      const session = sdkInstance.createPayLaterOneTimePaymentSession({
        onApprove: () => {
          this.$emit('paypal-payment-success');
          return Promise.resolve();
        },
        onCancel: () => {},
        onError: () => this.$emit('paypal-payment-error'),
      });

      const button = document.createElement('paypal-pay-later-button');
      button.countryCode = countryCode;
      button.productCode = productCode;
      button.style.display = 'block';
      button.style.width = '100%';
      button.addEventListener('click', async () => {
        try {
          await session.start({ presentationMode: 'auto' }, this.createOrder());
        } catch (error) {
          this.$emit('paypal-payment-error');
        }
      });
      this.$refs.buttonContainer.appendChild(button);
      this.showButtons = true;
    },

    setupCredit(sdkInstance, methods) {
      if (!methods.isEligible('credit')) {
        return;
      }
      // The credit button only needs countryCode (from eligibility details) to render.
      const { countryCode } = methods.getDetails('credit');

      const session = sdkInstance.createPayPalCreditOneTimePaymentSession({
        onApprove: () => {
          this.$emit('paypal-payment-success');
          return Promise.resolve();
        },
        onCancel: () => {},
        onError: () => this.$emit('paypal-payment-error'),
      });

      const button = document.createElement('paypal-credit-button');
      button.countryCode = countryCode;
      button.style.display = 'block';
      button.style.width = '100%';
      button.addEventListener('click', async () => {
        try {
          await session.start({ presentationMode: 'auto' }, this.createOrder());
        } catch (error) {
          this.$emit('paypal-payment-error');
        }
      });
      this.$refs.buttonContainer.appendChild(button);
      this.showButtons = true;
    },

    setupCardFields(sdkInstance, methods) {
      // 'advanced_cards' is the v6 funding key for hosted (unbranded) card fields — requires
      // Advanced Card Payments enabled on the merchant's PayPal account.
      if (!methods.isEligible('advanced_cards')) {
        console.log('PayPal advanced card fields are not eligible for this merchant / buyer.');
        return;
      }
      this.cardSession = sdkInstance.createCardFieldsOneTimePaymentSession();

      this.$refs.numberField
        .appendChild(this.cardSession.createCardFieldsComponent({ type: 'number' }));
      this.$refs.expiryField
        .appendChild(this.cardSession.createCardFieldsComponent({ type: 'expiry' }));
      this.$refs.cvvField
        .appendChild(this.cardSession.createCardFieldsComponent({ type: 'cvv' }));
      this.showCard = true;
    },

    async payWithCard() {
      if (!this.cardSession || this.submitting) {
        return;
      }
      this.submitting = true;
      try {
        // v6 flow: create the PayPal order, then submit the card fields against that order id.
        const { orderId } = await this.createOrder();
        // submit() resolves with { state, data }; state is 'succeeded' | 'failed' | 'canceled'.
        const response = await this.cardSession.submit(orderId);
        if (response.state === 'succeeded') {
          this.$emit('paypal-payment-success');
        } else {
          this.submitting = false;
          this.$emit('paypal-payment-error');
        }
      } catch (error) {
        this.submitting = false;
        this.$emit('paypal-payment-error');
      }
    },

    // ---- Save without purchase (PayPal Vault) ----

    // The v6 createVaultSetupToken callback: fetch a vault setup token from the backend and
    // resolve to the { vaultSetupToken } shape start()/submit() expect. `source` is 'paypal'
    // or 'card' so the backend builds the right (empty) payment source.
    async createVaultSetupToken(source) {
      const { data } = await axios.post(`${apiUrl}/orders/paypal/setup-token`, {
        order_id: this.orderId,
        source,
      }, {
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      });
      return { vaultSetupToken: data.data.setup_token };
    },

    // After approval, hand the approved setup token to the backend, which exchanges it for a
    // permanent vault payment token and persists it against the customer.
    async persistPaymentToken(setupTokenId) {
      await axios.post(`${apiUrl}/orders/paypal/payment-token`, {
        order_id: this.orderId,
        setup_token_id: setupTokenId,
      }, {
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      });
    },

    setupPaypalSave(sdkInstance, methods) {
      if (!methods.isEligible('paypal')) {
        return;
      }
      const session = sdkInstance.createPayPalSavePaymentSession({
        onApprove: async (data) => {
          try {
            await this.persistPaymentToken(data.vaultSetupToken);
            this.$emit('paypal-save-success');
          } catch (error) {
            this.$emit('paypal-save-error');
          }
        },
        onCancel: () => {},
        onError: () => this.$emit('paypal-save-error'),
      });

      const button = document.createElement('paypal-button');
      button.type = 'pay';
      button.style.display = 'block';
      button.style.width = '100%';
      button.addEventListener('click', async () => {
        try {
          // Second arg is the createVaultSetupToken promise ({ vaultSetupToken }).
          await session.start({ presentationMode: 'auto' }, this.createVaultSetupToken('paypal'));
        } catch (error) {
          this.$emit('paypal-save-error');
        }
      });
      this.$refs.savePaypalContainer.appendChild(button);
      this.showSavePaypal = true;
    },

    setupCardFieldsSave(sdkInstance, methods) {
      if (!methods.isEligible('advanced_cards')) {
        return;
      }
      // Do NOT mount a second card-fields session alongside the pay card fields — two
      // concurrent card-fields sessions on one SDK instance aren't a supported pattern and
      // the second set of iframes may fail to render. Pay takes precedence; the save-card
      // form is only mounted when the pay card form is not. A proper pay-vs-save toggle is a
      // product decision (see also the missing "offer save" gate).
      if (this.showCard) {
        return;
      }
      this.saveCardSession = sdkInstance.createCardFieldsSavePaymentSession();

      this.$refs.saveNumberField
        .appendChild(this.saveCardSession.createCardFieldsComponent({ type: 'number' }));
      this.$refs.saveExpiryField
        .appendChild(this.saveCardSession.createCardFieldsComponent({ type: 'expiry' }));
      this.$refs.saveCvvField
        .appendChild(this.saveCardSession.createCardFieldsComponent({ type: 'cvv' }));
      this.showSaveCard = true;
    },

    async saveCard() {
      if (!this.saveCardSession || this.savingCard) {
        return;
      }
      this.savingCard = true;
      try {
        // Card vault: mint a setup token, submit the card fields against it, then persist.
        const { vaultSetupToken } = await this.createVaultSetupToken('card');
        // submit() resolves with { state, data }; state is 'succeeded' | 'failed' | 'canceled'.
        const response = await this.saveCardSession.submit(vaultSetupToken);
        if (response.state === 'succeeded') {
          await this.persistPaymentToken(response.data.vaultSetupToken);
          this.savingCard = false;
          this.$emit('paypal-save-success');
        } else {
          this.savingCard = false;
          this.$emit('paypal-save-error');
        }
      } catch (error) {
        this.savingCard = false;
        this.$emit('paypal-save-error');
      }
    },
  },
};
</script>

<style lang="scss" module>
.embedded {
  margin-bottom: 14px;
}

.buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
}

.label {
  display: block;
  margin: 8px 0 4px;
  font-size: 12px;
  color: $ui-color-grey47;
}

.field {
  height: 40px;
  border: 1px solid $ui-color-grey87;
  border-radius: 4px;
  padding: 0 8px;
}

.row {
  display: flex;
  gap: 12px;
}

.col {
  flex: 1;
}

.saveBlock {
  margin-top: 20px;
  padding-top: 14px;
  border-top: 1px solid $ui-color-grey87;
}

.saveHeading {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: $ui-color-grey47;
}
</style>
